import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import LoadingSpinner from '../common/LoadingSpinner';

const GymTimelineModal = ({ isOpen, onClose, gymId, gymName }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timelineData, setTimelineData] = useState(null);

  useEffect(() => {
    if (isOpen && gymId) {
      fetchGymTimeline();
    }
  }, [isOpen, gymId]);

  const fetchGymTimeline = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/ceo/gym-timeline/${gymId}`);
      setTimelineData(response.data);
    } catch (error) {
      console.error('Error fetching gym timeline:', error);
      setError(error.response?.data?.error || 'Failed to fetch timeline');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTimelineData(null);
    onClose();
  };

  if (!isOpen) return null;

  const getStageColor = (stage) => {
    const colors = {
      'New Lead': { bg: 'bg-gray-100', border: 'border-gray-400', text: 'text-gray-800', dot: 'bg-gray-400' },
      'Site Visit': { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', dot: 'bg-blue-500' },
      'Proposal Sent': { bg: 'bg-yellow-100', border: 'border-yellow-400', text: 'text-yellow-800', dot: 'bg-yellow-500' },
      'Agreement Signed': { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', dot: 'bg-green-500' },
      'Facility Work': { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', dot: 'bg-purple-500' },
      'Post Reki Approved': { bg: 'bg-indigo-100', border: 'border-indigo-400', text: 'text-indigo-800', dot: 'bg-indigo-500' },
      'Gym Onboard': { bg: 'bg-red-100', border: 'border-red-400', text: 'text-red-800', dot: 'bg-red-500' }
    };
    return colors[stage] || colors['New Lead'];
  };

  const calculateDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Same day';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  const renderTimeline = () => {
    if (!timelineData?.stageHistory || timelineData.stageHistory.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No stage history available</p>
        </div>
      );
    }

    return (
      <div className="relative">
        {/* Timeline line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        <div className="space-y-6">
          {/* Created Event */}
          <div className="relative flex items-start">
            <div className="flex-shrink-0 w-12 flex justify-center">
              <div className="w-4 h-4 rounded-full bg-green-500 border-4 border-white shadow"></div>
            </div>
            <div className="ml-4 flex-1">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-green-800">Gym Created</h4>
                    <p className="text-sm text-green-600 mt-1">
                      Initial stage: {timelineData.gym.initialStage || 'New Lead'}
                    </p>
                  </div>
                  <div className="text-right text-sm text-green-600">
                    {format(new Date(timelineData.gym.createdAt), 'MMM dd, yyyy')}
                    <div className="text-xs">{format(new Date(timelineData.gym.createdAt), 'hh:mm a')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stage History */}
          {timelineData.stageHistory.map((event, index) => {
            const stageColor = getStageColor(event.toStage);
            const prevEvent = index > 0 ? timelineData.stageHistory[index - 1] : null;
            const duration = prevEvent
              ? calculateDuration(prevEvent.changedAt, event.changedAt)
              : calculateDuration(timelineData.gym.createdAt, event.changedAt);

            return (
              <div key={event.id} className="relative flex items-start">
                <div className="flex-shrink-0 w-12 flex justify-center">
                  <div className={`w-4 h-4 rounded-full ${stageColor.dot} border-4 border-white shadow`}></div>
                </div>
                <div className="ml-4 flex-1">
                  <div className={`${stageColor.bg} border ${stageColor.border} rounded-lg p-4`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className={`font-medium ${stageColor.text}`}>
                          {event.fromStage} → {event.toStage}
                        </h4>
                        {event.changer && (
                          <p className="text-sm text-gray-600 mt-1">
                            Changed by: {event.changer.name}
                            {event.changer.role?.name && (
                              <span className="text-gray-400"> ({event.changer.role.name})</span>
                            )}
                          </p>
                        )}
                        {event.notes && (
                          <p className="text-sm text-gray-500 mt-2 italic">"{event.notes}"</p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className={`text-sm ${stageColor.text}`}>
                          {format(new Date(event.changedAt), 'MMM dd, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(event.changedAt), 'hh:mm a')}
                        </div>
                        {duration && (
                          <div className="text-xs text-gray-400 mt-1">
                            After {duration}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Current Status */}
          {timelineData.gym.stage === 'Gym Onboard' && (
            <div className="relative flex items-start">
              <div className="flex-shrink-0 w-12 flex justify-center">
                <div className="w-6 h-6 rounded-full bg-red-500 border-4 border-white shadow flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <h4 className="font-bold text-red-800 text-lg">Gym Onboarded!</h4>
                      <p className="text-sm text-red-600 mt-1">
                        Gym acquisition successfully completed
                      </p>
                    </div>
                    <div className="text-4xl">🎉</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!timelineData?.gym) return null;

    const gym = timelineData.gym;
    const totalDays = calculateDuration(gym.createdAt, gym.updated_at);

    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-blue-600">{timelineData.stageHistory?.length || 0}</div>
          <div className="text-xs text-blue-700">Stage Changes</div>
        </div>
        <div className="bg-green-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-green-600">{totalDays || 'N/A'}</div>
          <div className="text-xs text-green-700">Total Duration</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-purple-600">{gym.stage}</div>
          <div className="text-xs text-purple-700">Current Stage</div>
        </div>
        <div className="bg-orange-50 rounded-lg p-3 text-center">
          <div className="text-xl font-bold text-orange-600">
            {gym.assignedUser?.name?.split(' ')[0] || 'Unassigned'}
          </div>
          <div className="text-xs text-orange-700">Assigned To</div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-800">
          <div>
            <h2 className="text-xl font-semibold text-white">
              {gymName || 'Gym'} Timeline
            </h2>
            <p className="text-sm text-blue-100 mt-1">Complete acquisition journey</p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2">{error}</div>
              <button
                onClick={fetchGymTimeline}
                className="text-blue-600 hover:text-blue-800 underline"
              >
                Try again
              </button>
            </div>
          ) : (
            <>
              {renderSummary()}
              {renderTimeline()}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default GymTimelineModal;
